import { StanButton } from '@/components';
import { cn } from '@/lib/utils/cn';

import { isEqual } from 'lodash';
import { HTMLAttributes, ReactNode, useState } from 'react';
import { useBlocks } from '../../hooks/useBlocks';
import { useDevice } from '../../hooks/useDevice';
import { useStoreItem } from '../../hooks/useStoreItem';
import { useCustomizationStore } from '../../stores/customizationStore';
import { useThemeEditorStore } from '../../stores/themeEditorStore';
import { ItemType, StoreItem } from '../../types/store';
import { useMenuNavigation } from '../Menu';
import { Icon } from '../ui/Icon';

export interface Props {
    className?: string;
    id?: number;
    children?: ReactNode;
    completeBtnLabel?: ReactNode;
}

export const CompleteButton = ({ children, className, onClick }: HTMLAttributes<HTMLButtonElement>) => {
    return (
        <StanButton name='done' size='sm' color='secondary' onClick={onClick} className={cn('!min-w-0', className)}>
            {children}
        </StanButton>
    );
};

export const RevertButton = ({ children, className, onClick }: HTMLAttributes<HTMLButtonElement>) => {
    return (
        <div className={cn('absolute left-1/2 -translate-x-1/2', className)}>
            <StanButton
                name='revert'
                onClick={onClick}
                color='secondary'
                variant='outline'
                size='sm'
                className='aspect-square !h-auto w-10 !min-w-0 !p-0'
                icon={<Icon name='revert' className='relative left-0.5' />}
            >
                {children}
            </StanButton>
        </div>
    );
};

export const PanelFooter = ({ children, className, id, completeBtnLabel = 'Done' }: Props) => {
    const { reset } = useMenuNavigation();
    const { deleteBlock } = useBlocks();
    const { updateItem, cachedState } = useCustomizationStore();
    const { setMenuOpen } = useThemeEditorStore();
    const item = useStoreItem(id);
    const [cachedItem] = useState<StoreItem | null>(cachedState?.storeItems?.find(item => item.id === id) ?? null);
    const { isMobile } = useDevice();

    const resetBlock = () => {
        if (cachedItem) {
            updateItem(cachedItem);
        }
    };

    const isDirty = !isEqual(cachedItem, item);

    const onClick = () => {
        if (isMobile) setMenuOpen(false);
        else reset();
    };

    return (
        <div
            className={cn(
                'bg-global-grey-10-stan border-neutral-subtle relative flex w-full flex-nowrap justify-between gap-2 border-t p-1 px-4 md:py-4',
                className
            )}
        >
            {id && item?.type !== ItemType.PAGE && (
                <StanButton
                    name='delete'
                    onClick={() => {
                        deleteBlock(id);
                        onClick();
                    }}
                    color='destructive'
                    variant='link'
                    size='sm'
                >
                    <Icon name='delete' className='text-[24px]' />
                </StanButton>
            )}

            {children}

            {cachedItem && isDirty && <RevertButton onClick={resetBlock} />}
            <div className='flex w-full flex-row justify-end'>
                <CompleteButton onClick={onClick}>{completeBtnLabel}</CompleteButton>
            </div>
        </div>
    );
};
